import { bookingActions } from 'src/app/features/booking/booking.actions'
import { Constants } from 'src/app/shared/constants'
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request'
import { Dictionary } from '@ngrx/entity'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core'
import { Store } from '@ngrx/store'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription
} from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import moment from 'moment-mini'
import DEBOUNCED_TIME = Constants.DEBOUNCED_TIME
import { AppState } from 'src/app/core/core.state'
import { settingsActions } from 'src/app/core/settings/settings.actions'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { BookableSlot } from 'src/app/features/bookable-slot/bookable-slot'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { BookingShowModalComponent } from 'src/app/features/booking/show-modal/show.component'
import { Product } from 'src/app/features/product/product'
import {
  ProductGroup,
  ProductGroupType
} from 'src/app/features/product/product-group/product-group'
import { productGroupActions } from 'src/app/features/product/product-group/product-group.actions'
import { productGroupSelectors } from 'src/app/features/product/product-group/product-group.selectors'
import { productActions } from 'src/app/features/product/product.actions'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { serieProposalSelectors } from '../../serie-proposal.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { SerieProposalItemService } from '../serie-proposal-item.service'
import { serieProposalItemSelectors } from '../serie-proposal-item.selectors'
import { SerieProposalItem } from '../serie-proposal-item'

interface ScheduleConfig {
  slotInterval: number
  openTime: string
  closeTime: string
  scheduleHeaderWidth: number
}
@UntilDestroy()
@Component({
  selector: 'serie-proposal-item-overview-schedule',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SerieProposalItemOverviewScheduleComponent implements OnInit {
  @Input() intersectingBookings: Booking[]
  @Output() onClickSerieProposalItem: EventEmitter<BookableSlot> =
    new EventEmitter()

  public form: UntypedFormGroup
  public selectedLanguage$: Observable<string>
  public selectedFilters$: Observable<any>
  public filterIsSelected$: Observable<boolean>
  public defaultStarDay = moment().startOf('year')
  public selectedBookableProductIds$: Observable<number[]>
  public selectedProductGroupIds$: Observable<number[]>
  public allProducts$: Observable<Product[]>
  public productEntities$: Observable<Dictionary<Product>>
  public bookableProducts$: Observable<Product[]>
  public productGroups$: Observable<ProductGroup[]>
  public latestBookableSlots$: Subject<BookableSlot[]> = new Subject<
    BookableSlot[]
  >()
  public bookableSlotsAndSerieProposalItems$: Observable<BookableSlot[]>
  public targetedProductsIds$: Observable<any>
  public scheduleConfig$: Observable<ScheduleConfig>
  public selectedDay$: Subject<moment.Moment> = new BehaviorSubject(
    moment().startOf('day')
  )
  public bsModalRef: BsModalRef

  private subscriptions: Subscription[] = []
  private latestBookableSlots: BookableSlot[] = []

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private modalService: BsModalService,
    private changeDetector: ChangeDetectorRef,
    private serieProposalItemService: SerieProposalItemService
  ) {}

  ngOnInit(): void {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.selectedFilters$ = this.store
      .select(settingSelectors.selectById, 'selectedFilters')
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        tap((item) => {
          this.form.controls.selectedBookableProductIds.setValue(
            item?.selectedBookableProductIds
          )
          this.form.controls.selectedProductGroupIds.setValue(
            item?.selectedProductGroupIds
          )
        })
      )

    this.form = this.fb.group({
      selectedProductGroupIds: [null, []],
      selectedBookableProductIds: [null, []]
    })

    this.selectedBookableProductIds$ =
      this.form.controls.selectedBookableProductIds.valueChanges

    this.selectedProductGroupIds$ =
      this.form.controls.selectedProductGroupIds.valueChanges

    this.filterIsSelected$ = combineLatest([
      this.selectedBookableProductIds$,
      this.selectedProductGroupIds$
    ]).pipe(
      withLatestFrom(this.selectedFilters$),
      debounceTime(10),
      tap(
        ([
          [selectedBookableProductIds, selectedProductGroupIds],
          selectedFilters
        ]) => {
          if (
            this.isFiltersEmpty(
              selectedBookableProductIds,
              selectedProductGroupIds
            )
          ) {
            return []
          }

          const targetFilters = {
            selectedBookableProductIds,
            selectedProductGroupIds
          }

          if (
            JSON.stringify(targetFilters) != JSON.stringify(selectedFilters)
          ) {
            this.store.dispatch(
              settingsActions.setOneById({
                id: 'selectedFilters',
                value: targetFilters
              })
            )
          }
        }
      ),

      map(([[a, b]]) => Boolean(a?.length || b?.length)),
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b))
    )

    this.targetedProductsIds$ = combineLatest([
      this.selectedBookableProductIds$,
      this.selectedProductGroupIds$,
      this.selectedFilters$,
      this.store.select(productGroupSelectors.selectEntities)
    ]).pipe(
      map(
        ([
          selectedBookableProductIds,
          selectedProductGroupIds,
          selectedFilters,
          productGroupEntities
        ]) => {
          selectedProductGroupIds = selectedProductGroupIds ?? []
          let targetedProductGroupProductIds = []
          selectedProductGroupIds?.map(
            (productGroupId) =>
              (targetedProductGroupProductIds = [
                ...targetedProductGroupProductIds,
                ...(productGroupEntities[productGroupId]?.products ?? [])
              ])
          )
          return [
            ...(selectedBookableProductIds ?? []),
            ...targetedProductGroupProductIds
          ]
        }
      )
    )

    combineLatest([this.targetedProductsIds$, this.selectedDay$])
      .pipe(untilDestroyed(this), debounceTime(DEBOUNCED_TIME))
      .subscribe(([targetedProductsIds, selectedDay]) => {
        console.log(
          `Will fetch bookings from range ${selectedDay?.toISOString()} - ${selectedDay
            .clone()
            .add(24, 'hours')
            .toISOString()}`
        )
      })

    this.bookableSlotsAndSerieProposalItems$ = combineLatest([
      this.selectedProductGroupIds$,
      this.store.select(productGroupSelectors.selectEntities),
      this.store.select(bookingSelectors.selectAll),
      this.selectedDay$,
      this.targetedProductsIds$,
      this.store.select(productSelectors.selectEntities),
      this.store.select(serieProposalItemSelectors.selectAll)
    ]).pipe(
      map(
        ([
          productGroups,
          productGroupEntities,
          bookings,
          selectedDay,
          targetedProductsIds,
          productEntities,
          serieProposalItems
        ]) => {
          const defaultSkeleton = this.createSkeleton(
            targetedProductsIds,
            selectedDay,
            productEntities
          )

          let bookingsArray: (Booking | BookableSlot)[] = bookings

          this.store
            .select(serieProposalSelectors.selectIdRouteParam)
            .pipe(take(1))
            .subscribe((serieProposalId) => {
              const serieProposalItemsAsBookableSlots: BookableSlot[] =
                serieProposalItems
                  .filter((item: SerieProposalItem) => {
                    const isSameDay =
                      moment(selectedDay).format('YYYY-MM-DD') ===
                      moment(item.startDate).format('YYYY-MM-DD')

                    const isWithinDateRange =
                      moment(selectedDay).isBetween(
                        moment(item.startDate).startOf('day'),
                        moment(item.endDate).endOf('day')
                      ) ||
                      moment(selectedDay).isSame(item.startDate, 'day') ||
                      moment(selectedDay).isSame(item.endDate, 'day')

                    return (
                      item.serieProposalId == serieProposalId &&
                      (isSameDay || isWithinDateRange)
                    )
                  })
                  .map((item) => {
                    return {
                      bookingId: null,
                      startDate: item.startDate,
                      endDate: item.endDate,
                      name: `${
                        productEntities[item.linkedProductId]?.description ?? ''
                      } ${productEntities[item.productId]?.description} - `,
                      isAvailable: true,
                      maxParticipants: 0,
                      availableParticipantCount: 0,
                      currentParticipantCount: 0,
                      bookableProductId: item.productId,
                      bookableProductDescription:
                        productEntities[item.productId]?.description,
                      linkedProductId: item.linkedProductId,
                      linkedProductDescription:
                        productEntities[item.linkedProductId]?.description,
                      group: item.linkedProductId ?? item.productId,
                      dependantProducts:
                        productEntities[item.productId]?.dependantProducts,
                      isDependantProduct: false,
                      serieProposalItemId: item.id,
                      hasConflictingBooking: this.hasConflictingBooking(item)
                    }
                  })

              bookingsArray = [
                ...bookingsArray,
                ...serieProposalItemsAsBookableSlots
              ]
            })

          const filterBookingsByDate = bookingsArray.filter(
            (booking) =>
              moment(selectedDay).format('YYYY-MM-DD') ==
                moment(booking.startDate).format('YYYY-MM-DD') ||
              (moment(selectedDay).format('YYYY-MM-DD') <=
                moment(booking.endDate).format('YYYY-MM-DD') &&
                moment(selectedDay).format('YYYY-MM-DD') >=
                  moment(booking.startDate).format('YYYY-MM-DD'))
          )

          const filteredBookings = filterBookingsByDate.filter(
            (booking: any) =>
              targetedProductsIds.includes(booking.productId) ||
              targetedProductsIds.includes(booking.linkedProductId)
          )

          const mainFilteredValue = filteredBookings.map((booking: any) => {
            return {
              bookingId: booking.id,
              startDate: booking.startDate,
              endDate: booking.endDate,
              name: `${
                productEntities[booking.linkedProductId]?.description ?? ''
              } ${productEntities[booking.productId]?.description} - `,
              isAvailable: true,
              maxParticipants: booking.maxParticipants || 0,
              availableParticipantCount: booking.availableParticipantCount || 0,
              currentParticipantCount: booking.currentParticipantCount || 0,
              bookableProductId: booking.productId,
              bookableProductDescription:
                productEntities[booking.productId]?.description,
              linkedProductId: booking.linkedProductId,
              linkedProductDescription:
                productEntities[booking.linkedProductId]?.description,
              group: booking.linkedProductId ?? booking.productId,
              dependantProducts:
                productEntities[booking.productId]?.dependantProducts,
              isDependantProduct: false,
              serieProposalItemId: (booking as any).serieProposalItemId ?? null,
              hasConflictingBooking:
                (booking as any).hasConflictingBooking ?? false
            }
          })

          const groupFilteredValue = mainFilteredValue
            .map((data) => {
              if (data.dependantProducts?.length > 0) {
                return data.dependantProducts.map((products) => {
                  return {
                    bookingId: data.bookingId,
                    startDate: data.dependantProducts.includes(products)
                      ? data.startDate
                      : null,
                    endDate: data.dependantProducts.includes(products)
                      ? data.endDate
                      : null,
                    name: `${productEntities[products]?.description ?? ''} ${
                      productEntities[products]?.description
                    }`,
                    isAvailable: true,
                    bookableProductId: products,
                    maxParticipants: data.maxParticipants || 0,
                    availableParticipantCount:
                      data.availableParticipantCount || 0,
                    currentParticipantCount: data.currentParticipantCount || 0,
                    bookableProductDescription:
                      productEntities[products]?.description,
                    linkedProductId: products,
                    linkedProductDescription:
                      productEntities[products]?.description,
                    group: products,
                    isDependantProduct: true
                  }
                })
              }
            })
            .filter((data) => data !== undefined)

          let groupValueFiltered = []

          groupFilteredValue.map((bookings) => {
            groupValueFiltered.push(...bookings)
          })

          let totalItemsToReturn = [
            ...mainFilteredValue,
            ...groupValueFiltered
            // ...productGroupProductsFakeBookableSlots
          ]

          if (totalItemsToReturn?.length > 0) {
            //added skeletonItems if is necessary
            for (const itemGroup of defaultSkeleton) {
              if (
                !(
                  totalItemsToReturn
                    .map((item) => item.bookableProductId)
                    .includes(itemGroup) ||
                  totalItemsToReturn
                    .map((item) => item.linkedProductId)
                    .includes(itemGroup)
                )
              ) {
                totalItemsToReturn.push(itemGroup)
              }
            }
            this.latestBookableSlots = totalItemsToReturn
            this.latestBookableSlots$.next(totalItemsToReturn)
          } else {
            this.latestBookableSlots$.next(defaultSkeleton)
          }

          return totalItemsToReturn
        }
      )
    )
    //cambiar a suscription
    this.scheduleConfig$ = combineLatest([
      this.bookableSlotsAndSerieProposalItems$
    ]).pipe(
      map(([bookableSlots]) => {
        let scheduleConfig: ScheduleConfig = {
          slotInterval: 15,
          openTime: '08:00',
          closeTime: '23:00',
          scheduleHeaderWidth: 4
        }
        bookableSlots.map((booking) => {
          if (
            moment(booking.startDate).format('HH:mm') < scheduleConfig.openTime
          ) {
            scheduleConfig.openTime = moment(booking.startDate).format('HH:mm')
          }
        })
        return scheduleConfig
      })
    )
    //  this.store.dispatch(productActions.fetchMany({}))
    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          join: [{ field: 'products' }],
          search: {
            type: ProductGroupType.SERIE_PROPOSAL_GROUP
          }
        })
      })
    )

    this.allProducts$ = this.store.select(productSelectors.selectAll)
    this.productGroups$ = this.store
      .select(productGroupSelectors.selectAll)
      .pipe(
        map((productGroups) =>
          productGroups.filter(
            (productGroup) =>
              productGroup.type == ProductGroupType.SERIE_PROPOSAL_GROUP
          )
        )
      )
    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.bookableProducts$ = this.allProducts$.pipe(
      map((product) => {
        return product.filter((product) => product.bookable)
      })
    )
  }

  public hasConflictingBooking(serieProposalItem: SerieProposalItem): boolean {
    return this.serieProposalItemService.hasConflictingBooking(
      serieProposalItem,
      this.intersectingBookings
    )
  }

  private createSkeleton(
    targetedProductsIds: number[],
    selectedDay: moment.Moment,
    productEntities: Dictionary<Product>
  ): BookableSlot[] {
    let bookableSlots: BookableSlot[] = []
    for (const productId of targetedProductsIds) {
      bookableSlots.push({
        startDate: moment(selectedDay).toISOString(),
        endDate: moment(selectedDay).toISOString(),
        name: productEntities[productId]?.description ?? '',
        isAvailable: true,
        bookingId: null,
        maxParticipants: 0,
        availableParticipantCount: 0,
        currentParticipantCount: 0,
        bookableProductDescription:
          productEntities[productId]?.description ?? '',
        linkedProductId: productId,
        linkedProductDescription: productEntities[productId]?.description ?? '',
        group: productId,
        bookableProductId: productId,
        dependantProducts: productEntities[productId]?.dependantProducts,
        isDependantProduct: false
      })
    }
    return bookableSlots
  }

  public onClickEvent(slot: BookableSlot) {
    if (slot.bookingId) {
      this.bsModalRef = this.modalService.show(BookingShowModalComponent, {
        initialState: {
          bookingId: slot.bookingId
        },
        class: 'modal-xl modal-dialog-scrollable'
      })

      this.subscriptions.push(
        this.bsModalRef.content.updateBookings
          .pipe(take(1))
          // add timeout here
          .subscribe((updateBooking) => {
            if (updateBooking) {
              this.store.dispatch(
                bookingActions.fetchOneById({
                  id: slot.bookingId,
                  params: RequestQueryBuilder.create({
                    join: [{ field: 'participations' }]
                  })
                })
              )
            }
          })
      )
    }
  }

  public onSerieProposalItemClick(bookableSlotWithSerieProposalItemId) {
    this.onClickSerieProposalItem.emit(bookableSlotWithSerieProposalItemId)
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }

  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [{ field: 'products' }]
        })
      })
    )
  }
  public onSearchProductGroups(term: string) {
    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [{ field: 'products' }]
        })
      })
    )
  }

  private isFiltersEmpty(selectedProductGroupIds, selectedBookableProductIds) {
    return (
      !selectedProductGroupIds?.length && !selectedBookableProductIds?.length
    )
  }
  public onDaySelected(day: moment.Moment) {
    this.selectedDay$.next(day.clone())
  }
}
